import React from "react";
import { useAuth } from "@/context/AuthContext";
import {
  ShieldCheck, Buildings, MapTrifold, Presentation, UserFocus, Headset, Briefcase,
  Check, ArrowRight,
} from "@phosphor-icons/react";

const ROLES = [
  {
    key: "hotel_gm", label: "Management Company", sub: "Group Admin",
    Icon: Buildings, tint: "#002FA7", bg: "#E0E7FF",
    scope: "Everything, org-wide",
    duty: "Sets up and oversees the whole operation — properties, people and permissions.",
    can: [
      "Add & edit properties (room types, meeting rooms, rates)",
      "Create the team and assign properties to managers",
      "Change any team member's role",
      "See every property, lead, account, booking & contract",
      "Full reports and analytics across the org",
    ],
  },
  {
    key: "rm", label: "Regional Manager", sub: "Multi-property",
    Icon: MapTrifold, tint: "#7C3AED", bg: "#EDE9FE",
    scope: "Assigned properties",
    duty: "Runs sales across several properties — the day-to-day driver of business.",
    can: [
      "Prospect for new business",
      "Create leads & assign them to sales",
      "Manage Accounts and Bookings",
      "Function Diary & events calendar",
      "Email requirement links & contracts",
      "Negotiate RFPs",
    ],
  },
  {
    key: "gm", label: "General Manager", sub: "Single property",
    Icon: Presentation, tint: "#B45309", bg: "#FEF3C7",
    scope: "Assigned property",
    duty: "Runs sales for their property — same toolkit as a Regional Manager, one property.",
    can: [
      "Prospect for new business",
      "Create leads & assign them to sales",
      "Manage Accounts and Bookings",
      "Function Diary & events calendar",
      "Email requirement links & contracts",
      "Negotiate RFPs",
    ],
  },
  {
    key: "dos", label: "Director of Sales", sub: "Sales leadership",
    Icon: UserFocus, tint: "#047857", bg: "#D1FAE5",
    scope: "Assigned properties",
    duty: "Leads the sales effort and coaches the team on their properties.",
    can: [
      "Prospect for new business",
      "Create leads & assign them to sales",
      "Manage Accounts and Bookings",
      "Function Diary & events calendar",
      "Email requirement links & contracts",
      "Negotiate RFPs",
    ],
  },
  {
    key: "sales", label: "Sales Manager", sub: "Front-line sales",
    Icon: Briefcase, tint: "#0369A1", bg: "#E0F2FE",
    scope: "Own & assigned leads",
    duty: "Works the leads day to day and keeps every enquiry moving.",
    can: [
      "Prospect for new business",
      "Work their own and assigned leads",
      "Log calls, emails & activity",
      "Claim shared front-desk enquiries",
      "Manage Accounts and Bookings",
    ],
  },
  {
    key: "front_desk", label: "Front Desk", sub: "Per property",
    Icon: Headset, tint: "#6B7280", bg: "#F3F4F6",
    scope: "Single property",
    duty: "Captures walk-in and phone enquiries and hands them to the sales team.",
    can: [
      "One simple screen to log a guest enquiry",
      "Enquiries are shared to sales to claim",
    ],
  },
];

function RoleCard({ r, isMe }) {
  const { Icon } = r;
  return (
    <div data-testid={`role-card-${r.key}`}
      className={`border p-5 transition-colors ${isMe ? "border-[#002FA7] shadow-[6px_6px_0_0_#002FA7]" : "border-[#111827]"}`}>
      <div className="flex items-start gap-3">
        <span className="inline-flex items-center justify-center w-11 h-11 shrink-0" style={{ background: r.bg }}>
          <Icon size={22} weight="bold" color={r.tint} />
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display font-bold text-lg text-[#111827] leading-tight">{r.label}</h3>
            {isMe && <span className="text-[10px] font-semibold uppercase tracking-wider bg-[#002FA7] text-white px-1.5 py-0.5" data-testid="role-you-badge">You</span>}
          </div>
          <span className="overline text-[#4B5563]">{r.sub}</span>
        </div>
      </div>
      <p className="text-sm text-[#4B5563] mt-3">{r.duty}</p>
      <div className="mt-3">
        <span className="overline text-[#4B5563] flex items-center gap-1">Scope</span>
        <span className="text-sm font-semibold text-[#111827]">{r.scope}</span>
      </div>
      <ul className="mt-3 space-y-1.5">
        {r.can.map((c, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-[#111827]">
            <Check size={15} weight="bold" color={r.tint} className="mt-0.5 shrink-0" /> {c}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function RolesPage() {
  const { user } = useAuth();
  const flow = ["hotel_gm", "rm", "gm", "dos", "sales", "front_desk"];
  const flowLabels = { hotel_gm: "Admin", rm: "Regional Mgr", gm: "General Mgr", dos: "Dir. of Sales", sales: "Sales", front_desk: "Front Desk" };

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-10" data-testid="roles-page">
      <div className="flex items-center gap-2">
        <ShieldCheck size={22} weight="bold" color="#002FA7" />
        <span className="overline text-[#4B5563]">Reference</span>
      </div>
      <h1 className="font-display font-black text-4xl sm:text-5xl text-[#111827] mt-1">Roles &amp; Permissions</h1>
      <p className="text-base text-[#4B5563] mt-2 max-w-2xl">
        Who does what in RateBridge. Everyone works within their scope so leads, accounts and bookings stay organised as your team grows.
      </p>

      {/* hierarchy strip */}
      <div className="mt-6 border border-[#E5E7EB] bg-[#F8FAFF] p-4 overflow-x-auto" data-testid="roles-hierarchy">
        <div className="flex items-center gap-2 min-w-max">
          {flow.map((k, i) => (
            <React.Fragment key={k}>
              <span className={`px-3 py-1.5 text-sm font-semibold whitespace-nowrap ${user?.role === k ? "bg-[#002FA7] text-white" : "bg-white border border-[#E5E7EB] text-[#111827]"}`}>
                {flowLabels[k]}
              </span>
              {i < flow.length - 1 && <ArrowRight size={16} weight="bold" className="text-[#9CA3AF] shrink-0" />}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        {ROLES.map((r) => <RoleCard key={r.key} r={r} isMe={user?.role === r.key} />)}
      </div>

      <div className="mt-8 border-l-4 border-[#002FA7] pl-4 py-1 max-w-2xl">
        <p className="text-sm text-[#4B5563]">
          <b className="text-[#111827]">Need to change who can do what?</b> Only the Management Company / Group Admin can create team members,
          assign properties and change roles — from the <b>Team</b> area on the dashboard.
        </p>
      </div>
    </div>
  );
}
